/*
 * <cdx-dialog> – wrapper around a native <dialog> (top layer, focus trap and Escape for free).
 *
 *   <cdx-dialog>
 *     <dialog id="my-dialog" aria-labelledby="…"> … <button data-cdx-dialog-close>…</button> … </dialog>
 *   </cdx-dialog>
 *   <button type="button" data-cdx-dialog-open="my-dialog" aria-haspopup="dialog">Open</button>
 *
 * Openers can live anywhere on the page (also in Ajax-loaded content): one delegated listener.
 * Clicking the backdrop closes the dialog, so give the <dialog> no padding and put the content in a child.
 * Exposed as window.CdxDialog so other components can extend it.
 */
if (!customElements.get("cdx-dialog")) {
  window.CdxDialog = class CdxDialog extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector("dialog");
      if (!this.dialog) return;

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.dialog.addEventListener(
        "click",
        (event) => {
          if (event.target === this.dialog || event.target.closest("[data-cdx-dialog-close]")) {
            this.close();
          }
        },
        { signal }
      );
      this.dialog.addEventListener("close", () => this.onClose(), { signal });
      // Handle Escape ourselves: other scripts (e.g. the Pandectes cookie banner) call
      // preventDefault() on Escape, which blocks the native dialog cancel.
      this.dialog.addEventListener(
        "keydown",
        (event) => {
          if (event.key !== "Escape") return;
          event.preventDefault();
          this.close();
        },
        { signal }
      );
    }

    disconnectedCallback() {
      this.abortController?.abort();
      // Removed while open (e.g. section re-render in the editor): don't leave the page scroll-locked
      if (this.isOpen) document.body.classList.remove("cdx-dialog-open");
    }

    get isOpen() {
      return !!this.dialog?.open;
    }

    open(opener) {
      if (!this.dialog || this.dialog.open) return;
      this.opener = opener || document.activeElement;
      this.dialog.showModal();
      document.body.classList.add("cdx-dialog-open");
    }

    close() {
      if (this.dialog?.open) this.dialog.close();
    }

    onClose() {
      document.body.classList.remove("cdx-dialog-open");
      if (this.opener?.isConnected && !this.contains(document.activeElement)) {
        this.opener.focus({ preventScroll: true });
      }
      this.opener = null;
    }
  };

  customElements.define("cdx-dialog", window.CdxDialog);

  document.addEventListener("click", (event) => {
    const opener = event.target.closest("[data-cdx-dialog-open]");
    if (!opener) return;
    const host = document.getElementById(opener.dataset.cdxDialogOpen)?.closest("cdx-dialog");
    if (!host || typeof host.open !== "function") return;
    event.preventDefault();
    host.open(opener);
  });
}
